/* istanbul ignore file */
import React from 'react';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { CuratorWrapper } from 'src/components/organisms/CuratorWrapper/CuratorWrapper';
import { RibbonsPage } from 'src/components/templates/RibbonsPage/RibbonsPage';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import type { DepartmentBannerContent } from 'src/content/adaptDepartmentBannerContent';
import { adaptDepartmentBannerContent } from 'src/content/adaptDepartmentBannerContent';
import { getPageContent } from 'src/content/getPageContent';
import { transformModularContentToUISections } from 'src/content/transformModularContentToUISections';
import { transformSeo } from 'src/content/transformSeo';
import type { ContentPageResultProps, PageConfiguration, PageMetadata } from 'src/types/PageProps';
import { getContentPageErrorResult } from 'src/utils/getContentPageErrorResult';
import { getPageMetadata } from 'src/utils/getPageMetadata';
import type { ContentstackSections } from '../../types/Contentstack';

const pageConfig: PageConfiguration = {
  env: process.env.NODE_ENV,
  contentType: 'department',
  pageType: 'department',
  trackingCategory: 'Department',
};

const separateDepartmentBanner = (modularContent: ContentstackSections[]) => {
  let departmentBannerContent: ContentstackSections | null = null;
  const remainingContent: ContentstackSections[] = [];

  modularContent.forEach((content) => {
    if ('department_banner' in content) {
      departmentBannerContent = content;
    } else {
      remainingContent.push(content);
    }
  });

  return { departmentBannerContent, remainingContent };
};

export const getServerSideProps: GetServerSideProps<ContentPageResultProps> = async (context) => {
  const { req } = context;

  const pageMetadata: PageMetadata = {
    ...getPageMetadata(context),
    functionalCookiesConsented: acceptsFunctionalCookies(req.cookies as CookieObj),
  };

  try {
    const { pageContent, siteChromeContent } = await getPageContent(pageConfig, pageMetadata);
    const { departmentBannerContent, remainingContent } = separateDepartmentBanner(
      pageContent.modular_content,
    );
    const departmentBanner = departmentBannerContent
      ? adaptDepartmentBannerContent(departmentBannerContent as unknown as DepartmentBannerContent)
      : null;

    return {
      props: {
        pageConfig,
        pageMetadata,
        pageContent: {
          ...(departmentBanner ? { departmentBanner } : {}),
          sections: await transformModularContentToUISections(
            remainingContent,
            pageConfig,
            pageMetadata,
          ),
          title: pageContent.title,
          seo: transformSeo(pageContent.seo, pageConfig, pageMetadata.pathname),
        },
        pageEventCategory: pageConfig.trackingCategory,
        siteChromeContent,
      },
    };
  } catch (error) {
    return getContentPageErrorResult(context.res, error);
  }
};

const DepartmentPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return (
    <>
      <CuratorWrapper />

      <div id={SKIP_CONTENT_TARGET_ID}>
        <RibbonsPage {...props} />
      </div>
    </>
  );
};

export default DepartmentPage;
